import { cloneDeep, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { IPayerData, IProposalPageFormState } from './form';
import ProposalPolicyPersonDrawer from '../../proposal-policy-person-drawer/ProposalPolicyPersonDrawer';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  fetchedPersonAddressList,
  fetchedPersonsWithPagination,
  GET_USER_ASSIGNED_BPS,
} from '../queries';
import { extractPersonsFromQuery, extractPersonsAddresses } from '../utils';
import { useParams } from 'react-router-dom';
import { validatePolicyPayerField } from '../../page/expat/validation';
import { IProposalDetailsSummary } from './index2';
import PersonAddressDrawer from '@modules/salesforce/persons/person-details-page/person-address-drawer/PersonAddressDrawer';
import AutocompleteFormField from '@components/form-fields/AutocompleteFormField';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import { IAbstractRecord, IPagination } from '@models';
import { useResetKey } from '../../../../../hooks/useResetKey';
import SwitchFormField from '@components/form-fields/SwitchFormField';
import { MAIN_THEME } from '@styles';
import { useAppSelector } from '@redux/hooks';

interface IPolicyDetailsWidgetProps {
  pageState: IProposalPageFormState;
  onPageStateUpdate: (pageState: IProposalPageFormState) => void;
  disabledForm?: boolean;
  data: IProposalDetailsSummary;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: `repeat(2, 49%)`,
    gap: '2%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignContent: 'ce',
  },
  field: {
    width: '100%',
    marginRight: '25px !important',
    minWidth: '195px',
  },
  orderField: {
    width: '100%',
    marginRight: '25px !important',
    minWidth: '20px',
  },
  sumInsuredRow: {
    width: '100%',
  },
  section: {
    display: 'grid',
    'grid-template-columns': '23.5% 23.5% 23.5% 23.5%',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    '& label': {
      '& span': {
        margin: '0 0 0',
      },
    },
  },
  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    padding: '0',
    margin: '20px auto 0',
    display: 'block',
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    padding: '0',
    margin: '0 auto',
    display: 'block',
  },
  labelHeader: {
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
  },
}));

const PolicyPayerWidget: React.FC<IPolicyDetailsWidgetProps> = ({
  pageState,
  onPageStateUpdate,
  disabledForm,
  data,
}) => {
  const { classes } = useStyles();
  const params = useParams();
  const entityId = params.id;
  const [autocompleteKey, resetAutocomplete] = useResetKey('payer');

  const { businessUser_Id } = useAppSelector((state) => state.user.info);

  const { data: assignedBPData, loading: bpLoading } = useQuery(
    GET_USER_ASSIGNED_BPS,
    {
      variables: { selectedBusinessUserID: businessUser_Id },
    }
  );

  const assignedBPIds = useMemo(() => {
    return assignedBPData?.SalesforceManagement?.queries?.getAssignedBPListWithoutPagination?.map(
      (bp: { businessPartner_Id: string }) => bp.businessPartner_Id
    );
  }, [assignedBPData]);

  useEffect(() => {
    resetAutocomplete();
  }, [assignedBPIds]);

  const [fetchedPersonsLazy, { loading: personsLoading }] = useLazyQuery(
    fetchedPersonsWithPagination()
  );
  const [getPersonAddressListLazy] = useLazyQuery(fetchedPersonAddressList());

  const [newPersonDrawerOpen, setNewPersonDrawerOpen] =
    useState<boolean>(false);
  const [newAddressDrawerOpen, setNewAddressDrawerOpen] =
    useState<boolean>(false);
  const [currentSection, setCurrentSection] = useState<string | null>(null);

  const [lists, setLists] = useState<
    Record<string, Record<string, Record<string, string> | string>>
  >({
    payerId: {
      payerPersons: {},
    },
    payerAddresses: {},
  });

  const values = pageState.values.policyPersons.payer;
  const errors = pageState.errors.policyPersons.payer;
  const touched = pageState.touched.policyPersons.payer;

  const handleOpenDrawer = (section: string) => {
    setCurrentSection(section);
    setNewPersonDrawerOpen(true);
  };

  const handleOpenAddressDrawer = () => {
    setNewAddressDrawerOpen(true);
  };

  const initialize = async () => {
    const addressList: Record<string, string> = {};
    addressList[values.payerAddress] = values.addressDetails;
    setLists({
      payerAddresses: addressList,
    });
  };

  useEffect(() => {
    initialize();
  }, []);

  const handlePayerAddresses = async (personId: string) => {
    const result: IAbstractRecord = await getPersonAddressListLazy({
      variables: {
        selectedPersonID: personId,
      },
      errorPolicy: 'all',
    });

    return extractPersonsAddresses(result.data);
  };

  const onFieldBlur = (fieldName: string) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched.policyPersons.payer[fieldName] = true;
    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = async (
    fieldName: string,
    value: unknown,
    touched = false,
    updatedList: typeof lists = lists
  ) => {
    const newLists = { ...updatedList };
    const newPageState = cloneDeep(pageState);
    if (fieldName === 'payerId') {
      (newPageState.values.policyPersons.payer as any)[fieldName] = value;
      newPageState.errors.policyPersons.payer[fieldName] =
        validatePolicyPayerField(fieldName, value);
      if (touched) {
        newPageState.touched.policyPersons.payer[fieldName] = true;
      }

      if (!isEmpty(value)) {
        const result2 = (await handlePayerAddresses(value as string)) as Record<
          string,
          Record<string, { address: string; isPrimary: boolean }>
        >;
        const payerAddresses = result2.payerPersonAddress;
        const addressKeys = Object.keys(payerAddresses) || [];
        if (addressKeys.length === 0) {
          newPageState.values.policyPersons.payer.payerAddress = '';
          newPageState.values.policyPersons.payer.fullName = '';
          newPageState.values.policyPersons.payer.addressDetails = '';
        }
        const extractedAddresses: IAbstractRecord = {};
        Object.keys(payerAddresses).forEach((key) => {
          extractedAddresses[key] = payerAddresses[key].address;
          if (payerAddresses[key].isPrimary) {
            newPageState.values.policyPersons.payer.payerAddress = key;
          }
        });
        newPageState.errors.policyPersons.payer.payerAddress = '';
        newLists['payerAddresses'] = extractedAddresses;
      }
    } else {
      (newPageState.values.policyPersons.payer as any)[fieldName] = value;
      newPageState.errors.policyPersons.payer[fieldName] =
        validatePolicyPayerField(fieldName, value);
      if (touched) {
        newPageState.touched.policyPersons.payer[fieldName] = true;
      }
    }
    setLists(newLists);
    onPageStateUpdate(newPageState);
  };

  const handleDrawerSubmitSuccess = (
    person: string,
    newPersonFullName: string
  ) => {
    const newPersonList: Record<string, string> = {};
    newPersonList[person] = newPersonFullName;
    onFieldUpdate('payerId', person, false, {
      payerId: { payerPersons: newPersonList },
      payerAddresses: {},
    }).finally(() => {
      resetAutocomplete();
      setNewPersonDrawerOpen(false);
    });
  };

  const handleAddressSubmitSuccess = (
    address: string,
    addressFullName: string
  ) => {
    onFieldUpdate('payerAddress', address, false, {
      payerAddresses: { [address]: addressFullName },
    });
    setNewAddressDrawerOpen(false);
  };

  const getDefaultPersonListIfEmpty = (
    list: Record<string, string>,
    payer: IPayerData
  ) => {
    if (Object.keys(list || {}).length > 0 || !payer.fullName) {
      return list;
    }

    const newList: Record<string, string> = {
      [payer.payerId]: payer.fullName,
      [payer.payerAddress]: payer.addressDetails,
    };

    return newList;
  };

  return (
    <div>
      <div className={classes.fieldRow}>
        {newPersonDrawerOpen && currentSection === 'payer' && (
          <ProposalPolicyPersonDrawer
            open={newPersonDrawerOpen}
            onClose={() => setNewPersonDrawerOpen(false)}
            section={currentSection}
            planId={''}
            lineExternalCode={data?.LineId?.ExternalCode}
            onSuccess={() => undefined}
            proposalId={entityId}
            onSubmitSuccess={handleDrawerSubmitSuccess}
          />
        )}

        {newAddressDrawerOpen && (
          <PersonAddressDrawer
            open={newAddressDrawerOpen}
            onClose={() => setNewAddressDrawerOpen(false)}
            personId={values.payerId}
            onSubmitSuccess={handleAddressSubmitSuccess}
            onSuccess={() => undefined}
          />
        )}

        <WithLinkFormField
          linkText={disabledForm ? '' : 'Add Person'}
          addSpace={!disabledForm}
          onLinkClick={() => {
            handleOpenDrawer('payer');
          }}
        >
          <AutocompleteFormField
            key={autocompleteKey}
            name="payerId"
            title="Payer"
            placeholder="Payer"
            value={values.payerId}
            error={touched.payerId ? errors.payerId : ''}
            disabled={disabledForm}
            loader={personsLoading || bpLoading}
            material
            onChange={(v) => {
              onFieldUpdate('payerId', v);
            }}
            fetchOptions={function (
              value,
              pagination,
              hasDefaultValue
            ): Promise<{
              options: Record<string, string>;
              totalCount: number;
            }> {
              const defaultValue = hasDefaultValue ? value : null;

              return fetchedPersonsLazy({
                variables: {
                  searchKeyword: hasDefaultValue ? null : value,
                  pagination,
                  selectedPersonID: defaultValue,
                  selectedBusinessPartners: assignedBPIds,
                },
              }).then((result) => {
                const options = extractPersonsFromQuery(
                  result.data?.Production?.queries
                    ?.GetAssignedPersonsWithPagination?.items
                );
                return {
                  options,
                  totalCount:
                    result.data?.Production?.queries
                      ?.GetAssignedPersonsWithPagination?.paging?.totalCount,
                };
              });
            }}
          />
        </WithLinkFormField>

        <WithLinkFormField
          linkText={disabledForm ? '' : 'Add Address'}
          addSpace={!disabledForm}
          onLinkClick={() => {
            handleOpenAddressDrawer();
          }}
        >
          <SelectFormField
            key="payerAddress"
            name="payerAddress"
            title="Payer Address"
            placeholder="Payer Address"
            value={values.payerAddress}
            error={touched.payerAddress ? errors.payerAddress : ''}
            material
            disabled={disabledForm}
            onChange={(v) => {
              onFieldUpdate('payerAddress', v);
            }}
            selectOptions={getDefaultPersonListIfEmpty(
              lists.payerAddresses as Record<string, string>,
              values
            )}
          />
        </WithLinkFormField>
      </div>

      <div style={{ width: '235px' }}>
        <SwitchFormField
          key="ownerIsDifferent"
          name="ownerIsDifferent"
          title="Owner is different from Payer"
          value={values.ownerIsDifferent}
          error={touched.ownerIsDifferent ? errors.ownerIsDifferent : ''}
          onBlur={() => onFieldBlur('ownerIsDifferent')}
          onChange={(name, value) => {
            onFieldUpdate('ownerIsDifferent', value, true);
          }}
          className={classes.field}
          disabled={disabledForm}
        />
      </div>
    </div>
  );
};

export default PolicyPayerWidget;
